// eslint-disable-next-line max-classes-per-file
const q = require('queried');
const events = require('events-mixin');
const extend = require('xtend/mutable');
const keys = require('key-name');
const last = require('lodash/last');
const domify = require('domify');
const ext = require('deep-extend');
const relevancy = require('relevancy');
const {
  FacetController,
} = require('./facet-filter');
const {
  Filter,
} = require('./filter');

/**
 * Get search initial state from URL.
 *
 * @param element
 * @returns {{search: *, searchLocationLabel: (*|string)}}
 */
function getInitialState(element) {
  const selectedElements = q.all('.subway-selected', element);
  const subway = selectedElements.map((item) => item.getAttribute('data-value'));

  return {
    facetFilter: { subway },
  };
}

class SubwayController extends FacetController {
  constructor(...args) {
    super(...args);

    const initialState = getInitialState(this.view.element);

    this.model.update((state) => ext(state, initialState));
  }
}

SubwayController.prototype = Object.create(FacetController.prototype);

const visibilityStates = {
  VISIBLE: 0,
  ABOVE: 1,
  BELOW: 2,
};

/**
 * Get item position relative to parent scroll state
 * @param  {Element} item - child element
 * @return {Number}       - value from 'scrollPosition' enum
 */
function getVisibilityState(item) {
  const parent = item.parentNode;
  const offset = item.offsetTop;
  const childHeight = item.clientHeight;
  const parentHeight = parent.clientHeight;
  const scroll = parent.scrollTop;

  if (offset < scroll) {
    return visibilityStates.ABOVE;
  } if (offset + childHeight > scroll + parentHeight) {
    return visibilityStates.BELOW;
  }

  return visibilityStates.VISIBLE;
}

class SubwayStations extends Filter {
  constructor(...args) {
    super(...args);

    this.subwayInput = q('.subway-search', this.element);
    this.subwayStations = q.all('.subway-search-item', this.element);
    this.visibleStations = this.subwayStations;
    this.emptyElement = q('.subway-search-empty', this.element);

    this.state = {
      query: '',
      focusedElementIndex: 0,
    };

    this.bindEvents();
  }

  bindEvents() {
    this.events = events(this.element, this);
    this.events.bind('input .subway-search', 'setQuery');
    this.events.bind('keydown .subway-search', 'handleKeyDown');

    const list = q('.subway-search-suggestions', this.element);
    list.addEventListener('mousewheel', (e) => {
      e.preventDefault();
      list.scrollTop -= e.wheelDeltaY / 4;
    });

    const handleMouseEnter = (event, item) => {
      if (event.movementX || event.movementY) {
        const index = Number(item.getAttribute('data-index'));
        this.setState({
          focusedElementIndex: index,
        });
      }
    };

    this.subwayStations.forEach((item) => {
      item.addEventListener('mouseenter', (event) => handleMouseEnter(event, item));
    });
  }

  handleKeyDown(e) {
    if (e.keyCode === keys.enter) {
      const index = this.state.focusedElementIndex;
      const delegateTarget = q(`[data-index="${index}"]`, this.element);
      if (delegateTarget) {
        this.emitFor('toggle', {
          delegateTarget,
          preventDefault: () => {},
        });
      }
    } else if (e.keyCode === keys.down) {
      const nextIndex = this.state.focusedElementIndex + 1;
      const nextElem = q(`[data-index="${nextIndex}"]`, this.element);
      if (nextElem) {
        this.setState({
          focusedElementIndex: nextIndex,
        });

        this.updateListScroll();
      }
    } else if (e.keyCode === keys.up) {
      const nextIndex = this.state.focusedElementIndex - 1;
      const nextElem = q(`[data-index="${nextIndex}"]`, this.element);
      if (nextElem) {
        this.setState({
          focusedElementIndex: nextIndex,
        });

        this.updateListScroll();
      }
    } else if (e.keyCode === keys.escape) {
      e.stopPropagation();
      this.subwayInput.blur();
    } else if (e.keyCode === keys.backspace) {
      if (!this.subwayInput.value) {
        const lastSelected = last(q.all('.subway-selected', this.element));
        if (lastSelected) {
          this.emitFor('toggle', {
            delegateTarget: lastSelected,
            preventDefault: () => {},
          });
        }
      }
    }
  }

  emitFor(action, event) {
    super.emitFor(action, event);

    this.subwayInput.value = '';
    this.setState({
      query: '',
    });
  }

  setState(newState) {
    this.state = extend({}, this.state, newState);
    this.render();
  }

  setQuery(e) {
    const { value } = e.target;
    this.setState({
      query: value,
    });

    setImmediate(() => {
      this.setState({
        focusedElementIndex: 0,
      });
    });
  }

  testMatch(text) {
    const { query } = this.state;
    const regexStr = query
      .trim()
      .replace(/ё/g, 'е')
      .replace(/[-[\]/{}()*+?.\\^$|]/g, '\\$&');
    const regex = new RegExp(regexStr, 'i');
    return regex.test(this.yeoify(text));
  }

  yeoify(text) {
    return text.replace(/ё/g, 'е');
  }

  getMatchingStations() {
    return this.subwayStations.filter((item) => this.testMatch(item.textContent));
  }

  /**
   * Reset stations dom attributes, css classes, etc.
   * Necessary first step of rendering the component
   */
  resetStations() {
    this.subwayStations.forEach((item) => {
      // clean item's attributes
      item.setAttribute('hidden', 'hidden');
      item.classList.remove(this.ACTIVE_CLASS);
      item.removeAttribute('data-index');
    });
  }

  /**
   * Update list scroll if active item is not visible
   * @return {[type]} [description]
   */
  updateListScroll() {
    const item = this.element.querySelector(`.${this.ACTIVE_CLASS}`);

    if (item) {
      const visibilityState = getVisibilityState(item);
      const parent = item.parentNode;

      if (visibilityState === visibilityStates.ABOVE) {
        parent.scrollTop = item.offsetTop;
      } else if (visibilityState === visibilityStates.BELOW) {
        parent.scrollTop = item.offsetTop - parent.clientHeight + item.clientHeight;
      }
    }
  }

  /**
   * Update dom attributes, css classes, etc.
   * for stations matching the search query
   */
  renderMatchingStations() {
    const stations = this.getMatchingStations();
    const stationsTexts = stations.map((i) => this.yeoify(i.textContent));
    const sortedTexts = relevancy.sort(stationsTexts, this.yeoify(this.state.query));

    return stations.map((item) => {
      const index = sortedTexts.indexOf(this.yeoify(item.textContent));
      const isActive = index === this.state.focusedElementIndex;

      item.removeAttribute('hidden');

      if (isActive) {
        // add a class to currently active item
        item.classList.add(this.ACTIVE_CLASS);
      }

      // visually sort items by flexbox ordering
      item.style.order = index;
      item.setAttribute('data-index', index);

      return item;
    });
  }

  /**
   * Update state of 'nothing found' block
   */
  renderNothingFound() {
    const matchingStations = this.getMatchingStations();

    if (!matchingStations.length) {
      this.emptyElement.removeAttribute('hidden');
    } else {
      this.emptyElement.setAttribute('hidden', 'hidden');
    }
  }

  /**
   * Render the stations which are already chosen
   * @param  {Object} state - feed model instance
   */
  renderSelectedStations(state) {
    if (state) {
      const selectedStations = state.facetFilter.subway || [];
      const matchingStations = this.getMatchingStations();

      // make items dimmed in subway stations list
      matchingStations.forEach((i) => {
        if (selectedStations.includes(i.getAttribute('data-value'))) {
          i.classList.add(this.SELECTED_CLASS);
        } else {
          i.classList.remove(this.SELECTED_CLASS);
        }
      });

      // remove all the previously selected stations labels
      q.all('.subway-selected', this.element).forEach((item) => {
        item.parentNode.removeChild(item);
      });

      // render selected stations labels
      const selectedStationsElements = selectedStations.map((slug) => {
        const element = q(`[data-value="${slug}"]`, this.element);
        const name = element.textContent;

        return `
          <span
            data-mode="radio"
            data-key="subway"
            data-value="${slug}"
            class="subway-selected"
          >
            ${name}
          </span>
        `;
      }).join('');

      if (selectedStationsElements) {
        this.element.insertBefore(domify(selectedStationsElements), this.element.firstChild);
      }
    }
  }

  render(state) {
    this.resetStations();
    this.renderMatchingStations();
    this.renderNothingFound();

    if (state) {
      this.renderSelectedStations(state);
    }
  }
}

SubwayStations.prototype.ACTIVE_CLASS = 'subway-search-item--active';
SubwayStations.prototype.SELECTED_CLASS = 'subway-search-item--selected';

module.exports = {
  SubwayStations,
  SubwayController,
  getVisibilityState,
};
